import { each } from "async";
import { BaseWindow } from "../WindowAbstractions/BaseWindow";
import { WindowGroup } from "../windowGroup";
import { RouterClient } from "../../../../clients/routerClient";
import { FSBLWindow } from "../localTypes";

const groupStates = {
	NORMAL: 0,
	MINIMIZING: 1,
	MINIMIZED: 2,
	RESTORING: 3,
};

// NOTE: converting to TS, parts of this file looked suspect, so not sure if this code is actually used

// NOTE: this file provides a temporary solution for window groups that must operate off private wrappers (as opposed to public).
// Otherwise, the code is the same as in WindowGroup.  TBD is to reevaluate WindowGroup and it's usage.

export class LauncherGroup extends WindowGroup {
	name: string;

	groupState: number = groupStates.NORMAL;

	GROUPSTATES: typeof groupStates = groupStates;

	isAlwaysOnTop: boolean = false;

	windows: Record<string, FSBLWindow> = {};

	interruptRestore: boolean = false;

	constructor(params) {
		super(params);
		this.name = params.name;
		if (params.windows) {
			this.windows = params.windows;
		}
	}

	minimizeAll() {
		if (this.groupState == groupStates.RESTORING) {
			this.interruptRestore = true;
			this.groupState = groupStates.NORMAL;
		}
		if (this.groupState !== groupStates.NORMAL) return;
		this.groupState = groupStates.MINIMIZING;
		for (const windowName in this.windows) {
			const win = this.windows[windowName];
			if (
				win?.windowState != BaseWindow.WINDOWSTATE.MINIMIZED_WAS_NORMAL &&
				win?.windowState != BaseWindow.WINDOWSTATE.MINIMIZED_WAS_MAXIMIZED &&
				win?._minimize
			)
				win._minimize({});
		}
		this.groupState = groupStates.MINIMIZED;
		this.interruptRestore = false;
	}

	minimize(params: { windowList: FSBLWindow[]; componentType?: string }) {
		if (!params) {
			return this.minimizeAll();
		}
		let { windowList, componentType } = params;
		if (componentType) windowList = this.findAllByComponentType(componentType);

		if (windowList) {
			for (const w of windowList) {
				let win;
				if (!(typeof w === "string" || w instanceof String)) {
					win = this.getWindow(w.windowName || w.name);
				} else {
					win = this.getWindow(w);
				}
				// don't minimize child windows -- let stacked window do it for visible child
				if (
					win?.windowState != BaseWindow.WINDOWSTATE.MINIMIZED_WAS_NORMAL &&
					win?.windowState != BaseWindow.WINDOWSTATE.MINIMIZED_WAS_MAXIMIZED &&
					win?._minimize
				) {
					win._minimize();
				}
			}
		}
	}

	restoreAll(cb = Function.prototype) {
		if (this.groupState !== groupStates.MINIMIZED) return cb();
		const self = this;
		this.groupState = groupStates.RESTORING;
		function restoreWindow(windowName, done) {
			if (self.interruptRestore) return done("restore interrupted");
			const win = self.windows[windowName];
			if (win?.windowState != BaseWindow.WINDOWSTATE.NORMAL && win?._restore) win._restore({}, done);
			else done();
		}
		each(Object.keys(this.windows), restoreWindow, (err) => {
			self.interruptRestore = false;
			if (!err) {
				self.groupState = groupStates.NORMAL;
			}
			cb(err);
		});
	}

	// takes an array of window names.
	restore(params, cb) {
		const { windowList } = params;
		const self = this;
		function restoreWindow(windowName, done) {
			const win = self.windows[windowName];
			if (win?._restore) {
				win._restore({}, done);
			} else {
				done();
			}
		}
		each(windowList, restoreWindow, cb);
	}

	// Bring all windows to top. Also sets the state of the group to always on top and new windows added to the group inherit the state of thw window
	allAlwaysOnTop(alwaysOnTop) {
		this.isAlwaysOnTop = alwaysOnTop;
		this.alwaysOnTop({
			windowList: Object.keys(this.windows),
			restoreWindows: true,
			alwaysOnTop,
		});
	}

	// Set specific windows to top. Generally should call allAlwaysOnTop
	alwaysOnTop(params: {
		windowName?: string;
		componentType?: string;
		windowList?: string[] | FSBLWindow[];
		restoreWindows?: boolean;
		alwaysOnTop?: boolean;
	}) {
		if (!params || (params && Object.keys(params).length === 0)) {
			params = { windowList: Object.keys(this.windows), restoreWindows: true };
		}
		let { windowList, componentType } = params;
		if (windowList && typeof windowList[0] !== "string") {
			windowList = windowList.map((win) => win.windowName);
		}
		if (componentType) windowList = this.findAllByComponentType(componentType);
		const self = this;
		if (!windowList) windowList = Object.keys(this.windows);
		let w: FSBLWindow | string;
		for (w in windowList) {
			let win;
			if (Array.isArray(windowList)) w = windowList[w];

			if (!(typeof w === "string" || w instanceof String)) {
				win = self.getWindow(w.windowName || w.name);
			} else {
				win = self.getWindow(w);
			}
			if (win?.alwaysOnTop) {
				win.alwaysOnTop(params.alwaysOnTop);
			}
		}
	}

	/**
	 * Brings a group of windows to the front (BTF). In other words, puts those windows on top of any other windows so that they can be seen
	 * @param {object} params
	 * @param {bool=true} params.restoreWindows If true then windows will attempt to be restored (un-minimized) before being brought to front
	 * @param {array} params.windowList The list of windows to BTF. Defaults to the windows that are in this window group. This can be a list of window names, or a list of actual window instances.
	 * @param {string} params.componentType Optionally provide a componentType to BTF only those windows of that type in the list of windows.
	 */
	bringToFront(params, cb = Function.prototype) {
		const self = this;
		if (!params) params = {};
		if (typeof params.restoreWindows === "undefined") params.restoreWindows = true;

		let { windowList, componentType } = params;
		// Determine if the windowList is a list of window names, or a list of actual windows (in which case we extract the name)
		if (windowList && typeof windowList[0] !== "string") {
			windowList = windowList.map((win) => win.windowName);
		}

		// Get all windows *in this group* of this component type, then convert them into an array of strings to be passed into the other group functions.
		if (componentType) {
			windowList = [];
			const windows = this.findAllByComponentType(componentType);
			windows.forEach((win) => {
				if (win?.name) {
					windowList.push(win.name);
				}
			});
		}

		// Default to the windows in this group, actually the most common case
		if (!windowList) windowList = Object.keys(this.windows);

		function doBTF() {
			each(
				windowList,
				(w: FSBLWindow | string, callback) => {
					let win;
					// if (Array.isArray(windowList)) w = windowList[w];

					if (!(typeof w === "string" || w instanceof String)) {
						win = self.getWindow(w.windowName || w.name);
					} else {
						win = self.getWindow(w);
					}
					if (win?._bringToFront) {
						win._bringToFront(callback);
					} else {
						callback();
					}
				},
				() => {
					cb();
				}
			);
		}

		if (params.restoreWindows) {
			if (typeof windowList[0] !== "string") {
				windowList = Object.keys(windowList);
			}
			RouterClient.query("DockingService.restoreDockableWindows", { windowNames: windowList }, (err, response) => {
				const remainingWindows = response.data;
				this.restore({ windowList: remainingWindows }, doBTF);
			});
		} else {
			doBTF();
		}
	}

	findAllByComponentType(componentType) {
		const windowList: FSBLWindow[] = [];
		for (const windowName in this.windows) {
			const thisWindowComponentType = this.windows[windowName].componentType;
			if (
				componentType === thisWindowComponentType ||
				componentType === this.windows[windowName].windowDescriptor?.componentType
			) {
				windowList.push(this.windows[windowName]);
			}
		}
		return windowList;
	}
}
